export type NotificationTabMessage =
  | {
      type: 'presence';
      instanceId: string;
      sequence: number;
      isVisible: boolean;
      hasFocus: boolean;
      replyRequested: boolean;
    }
  | {
      type: 'handled';
      eventId: string;
      expiresAt: number;
    }
  | {
      type: 'leave';
      instanceId: string;
    };

type MessageListener = (event: { data: NotificationTabMessage }) => void;

export interface NotificationBroadcastChannel {
  postMessage(message: NotificationTabMessage): void;
  addEventListener(type: 'message', listener: MessageListener): void;
  removeEventListener(type: 'message', listener: MessageListener): void;
  close(): void;
}

export interface NotificationTabCoordinator {
  start(): void;
  stop(): void;
  isPresentationLeader(): boolean;
  hasHandled(eventId: string): boolean;
  markHandled(eventId: string): void;
  subscribe(listener: () => void): () => void;
}

interface NotificationTabCoordinatorOptions {
  instanceId?: string;
  channelFactory?: (name: string) => NotificationBroadcastChannel | null;
  getPageState?: () => { isVisible: boolean; hasFocus: boolean };
  now?: () => number;
  setInterval?: typeof globalThis.setInterval;
  clearInterval?: typeof globalThis.clearInterval;
}

interface Participant {
  sequence: number;
  isVisible: boolean;
  hasFocus: boolean;
  lastSeenAt: number;
}

const heartbeatMilliseconds = 2_000;
const participantExpiryMilliseconds = 5_000;
const handledExpiryMilliseconds = 60_000;
const channelName = 'gba.notifications';

const defaultChannelFactory = (
  name: string,
): NotificationBroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(name) as NotificationBroadcastChannel;
};

const defaultPageState = () => ({
  isVisible:
    typeof document !== 'undefined' && document.visibilityState === 'visible',
  hasFocus: typeof document !== 'undefined' && document.hasFocus(),
});

const defaultInstanceId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const createNotificationTabCoordinator = (
  subject: string,
  options: NotificationTabCoordinatorOptions = {},
): NotificationTabCoordinator => {
  const instanceId = options.instanceId ?? defaultInstanceId();
  const channelFactory = options.channelFactory ?? defaultChannelFactory;
  const getPageState = options.getPageState ?? defaultPageState;
  const now = options.now ?? Date.now;
  const setIntervalFn = options.setInterval ?? globalThis.setInterval;
  const clearIntervalFn = options.clearInterval ?? globalThis.clearInterval;
  const participants = new Map<string, Participant>();
  const handledEvents = new Map<string, number>();
  const listeners = new Set<() => void>();
  let channel: NotificationBroadcastChannel | null = null;
  let heartbeat: ReturnType<typeof setIntervalFn> | null = null;
  let sequence = 0;
  let started = false;

  const emit = () => listeners.forEach((listener) => listener());

  const prune = () => {
    const currentTime = now();
    participants.forEach((participant, id) => {
      if (
        id !== instanceId &&
        currentTime - participant.lastSeenAt >= participantExpiryMilliseconds
      )
        participants.delete(id);
    });
    handledEvents.forEach((expiresAt, eventId) => {
      if (expiresAt <= currentTime) handledEvents.delete(eventId);
    });
  };

  const publishPresence = (replyRequested: boolean) => {
    if (!channel) return;
    const state = getPageState();
    sequence += 1;
    participants.set(instanceId, {
      sequence,
      ...state,
      lastSeenAt: now(),
    });
    channel.postMessage({
      type: 'presence',
      instanceId,
      sequence,
      ...state,
      replyRequested,
    });
  };

  const onMessage: MessageListener = ({ data }) => {
    if (data.type === 'presence') {
      if (data.instanceId === instanceId) return;
      const existing = participants.get(data.instanceId);
      if (existing && existing.sequence >= data.sequence) return;
      participants.set(data.instanceId, {
        sequence: data.sequence,
        isVisible: data.isVisible,
        hasFocus: data.hasFocus,
        lastSeenAt: now(),
      });
      if (data.replyRequested) publishPresence(false);
      prune();
      emit();
      return;
    }
    if (data.type === 'handled') {
      if (data.expiresAt > now())
        handledEvents.set(data.eventId, data.expiresAt);
      prune();
      emit();
      return;
    }
    participants.delete(data.instanceId);
    emit();
  };

  const start = () => {
    if (started) return;
    started = true;
    channel = channelFactory(`${channelName}.${subject}`);
    if (!channel) return;
    channel.addEventListener('message', onMessage);
    publishPresence(true);
    heartbeat = setIntervalFn(() => {
      publishPresence(false);
      prune();
      emit();
    }, heartbeatMilliseconds);
  };

  const stop = () => {
    if (!started) return;
    started = false;
    if (heartbeat != null) clearIntervalFn(heartbeat);
    heartbeat = null;
    if (channel) {
      channel.postMessage({ type: 'leave', instanceId });
      channel.removeEventListener('message', onMessage);
      channel.close();
    }
    channel = null;
    participants.clear();
    handledEvents.clear();
    emit();
  };

  const isPresentationLeader = () => {
    if (!channel) {
      const state = getPageState();
      return state.isVisible && state.hasFocus;
    }
    prune();
    const state = getPageState();
    participants.set(instanceId, {
      sequence,
      ...state,
      lastSeenAt: now(),
    });
    const active = [...participants].sort(([left], [right]) =>
      left.localeCompare(right),
    );
    const focused = active.filter(
      ([, participant]) => participant.isVisible && participant.hasFocus,
    );
    return (focused[0] ?? active[0])?.[0] === instanceId;
  };

  const hasHandled = (eventId: string) => {
    prune();
    return handledEvents.has(eventId);
  };

  const markHandled = (eventId: string) => {
    const expiresAt = now() + handledExpiryMilliseconds;
    handledEvents.set(eventId, expiresAt);
    channel?.postMessage({ type: 'handled', eventId, expiresAt });
    emit();
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    start,
    stop,
    isPresentationLeader,
    hasHandled,
    markHandled,
    subscribe,
  };
};
